/*
 * Koopblok van de travel pouch
 * -----------------------------------------------------------------------------
 * De tegenhanger van bundle-picker.js, maar dan zonder tredes en zonder
 * geurkeuze: één product, een kleur en een aantal.
 *
 * Wat het doet: de gekozen optiewaarden omzetten naar een variant, de prijs en
 * de knop daarop bijwerken, de galerij meeschakelen als de variant een eigen
 * foto heeft, en de regel toevoegen aan het mandje.
 */

(function () {
  'use strict';

  /* Het producttype komt uit de admin; op hoofdletters valt niet te bouwen. */
  function sameGender(a, b) {
    return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  }

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  class PouchPicker extends HTMLElement {
    connectedCallback() {
      this.variants = this.readVariants();
      this.button = this.querySelector('[data-pouch-add]');
      this.errorNote = this.querySelector('[data-pouch-error]');
      this.quantityInput = this.querySelector('[data-quantity-input]');

      this.addLabel = this.dataset.addLabel || 'Add to cart';
      this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
      this.saveLabel = this.dataset.saveLabel || 'Save [percentage]%';

      /*
       * De bundel staat alleen aan als Liquid hem compleet bevonden heeft:
       * genoeg leverbare geuren en genoeg leverbare cadeauvarianten. Hier
       * wordt dat niet nog eens beoordeeld, alleen overgenomen.
       */
      this.bundle = this.dataset.bundleEnabled === 'true';
      this.mode = 'single';

      this.bindEvents();
      this.setupGallery();
      this.setupBundle();
      this.syncVariant();
      this.syncQuantityButtons();
      this.watchStickyBar();
    }

    readVariants() {
      const holder = this.querySelector('[data-variants]');
      if (!holder) return [];
      try {
        return JSON.parse(holder.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    bindEvents() {
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-quantity-input]')) {
          this.normaliseQuantity();
          return;
        }
        if (event.target.matches('.pouch-swatch__radio')) this.syncVariant();
      });

      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-quantity-input]')) this.syncQuantityButtons();
      });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-quantity-down]')) {
          this.stepQuantity(-1);
          return;
        }
        if (event.target.closest('[data-quantity-up]')) {
          this.stepQuantity(1);
          return;
        }
        if (event.target.closest('[data-pouch-add]')) {
          event.preventDefault();
          this.addToCart();
        }
      });
    }

    /* ------------------------------------------------------------- keuze -- */

    /* De gekozen waarde per optie, op volgorde van optiepositie. */
    selection() {
      return Array.prototype.map.call(this.querySelectorAll('[data-option-position]'), (group) => {
        const checked = group.querySelector('.pouch-swatch__radio:checked');
        return checked ? checked.value : '';
      });
    }

    findVariant(values) {
      return this.variants.find((variant) =>
        values.every((value, index) => variant.options[index] === value)
      );
    }

    syncVariant() {
      const values = this.selection();

      /*
       * Zonder opties (één variant) is er niets te kiezen; dan blijft de
       * variant staan die Liquid al in het verborgen veld heeft gezet.
       */
      const variant = values.length ? this.findVariant(values) : this.variants[0];

      this.paintSwatches(values);
      this.paintCardImages(values[0]);
      if (!variant) return;

      this.current = variant;

      this.paintPrice(variant);
      this.paintButton(variant);
      this.paintGallery(variant);
      this.paintUrl(variant);
      this.paintTabs();
      this.hideError();
    }

    /*
     * De rand om de gekozen kaart zetten we zelf en niet met :has(): dat scheelt
     * een aanname over de browser, en dezelfde lus markeert meteen welke kleuren
     * niet leverbaar zijn.
     */
    paintSwatches(values) {
      this.querySelectorAll('[data-option-position]').forEach((group, groupIndex) => {
        group.querySelectorAll('.pouch-swatch').forEach((swatch) => {
          const radio = swatch.querySelector('.pouch-swatch__radio');
          if (!radio) return;

          swatch.classList.toggle('is-selected', radio.checked);

          /*
           * Leverbaar betekent hier: er bestaat een voorradige variant met deze
           * waarde en de waarden die op de andere opties al gekozen zijn.
           */
          const candidate = values.slice();
          candidate[groupIndex] = radio.value;
          const match = this.variants.find((variant) =>
            candidate.every((value, index) => !value || variant.options[index] === value)
          );
          swatch.classList.toggle('is-unavailable', !match || !match.available);

          /*
           * De knop met dezelfde waarde in de vaste balk krijgt hetzelfde
           * stempel. Alleen voor de eerste optie: die balk toont de kleur, en
           * bij een tweede optie zouden twee groepen om dezelfde knop vechten.
           */
          if (groupIndex === 0) {
            /*
             * Vergelijken en niet in een selector plakken. Een optiewaarde komt
             * uit de winkel en mag alles bevatten -- een aanhalingsteken in
             * "Cognac 'special'" zou de selector breken, en CSS.escape is een
             * afhankelijkheid die deze lus niet nodig heeft. Klapt hier iets om,
             * dan valt de hele kleurkeuze uit.
             */
            document.querySelectorAll('[data-sticky-colour]').forEach((knop) => {
              if (knop.dataset.stickyColour !== radio.value) return;
              knop.classList.toggle('is-selected', radio.checked);
              knop.classList.toggle('is-unavailable', !match || !match.available);
            });
          }
        });
      });
    }

    /*
     * De fotokaarten verderop de pagina volgen de gekozen kleur.
     *
     * Die secties staan buiten dit element -- het zijn eigen secties -- dus ze
     * worden hier opgezocht en niet via this. Alleen kaarten met een tweede
     * foto doen mee; de rest heeft er geen, bijvoorbeeld omdat er al beide
     * kleuren op staan.
     *
     * Alleen de src wisselen en geen tweede img tonen: dan laadt de browser
     * niet elke foto dubbel om er één van te laten zien.
     */
    paintCardImages(value) {
      if (!value) return;
      const gekozen = String(value).toLowerCase().trim();

      document.querySelectorAll('[data-card-image-alt]').forEach((img) => {
        const bij = (img.dataset.cardImageAltColour || '').toLowerCase().trim();
        const bron = gekozen === bij ? img.dataset.cardImageAlt : img.dataset.cardImage;
        if (bron && img.getAttribute('src') !== bron) img.setAttribute('src', bron);
      });
    }

    paintPrice(variant) {
      if (!variant) return;
      const now = this.querySelector('[data-price-now]');
      const was = this.querySelector('[data-price-was]');
      const save = this.querySelector('[data-price-save]');

      /*
       * Met een bundel is de losse prijsregel door setupBundle verborgen: elke
       * keuzekaart draagt zijn eigen bedrag en twee prijzen voor hetzelfde laat
       * de klant raden welke telt. Bijwerken mag hier gewoon doorgaan -- het is
       * goedkoop, en zo klopt het bedrag ook als de regel ooit weer tevoorschijn
       * komt. De bedragen die de klant ziet schrijft paintTabs.
       *
       * Zonder bundel is deze regel wel zichtbaar en de enige plek waar de
       * prijs vandaan komt.
       */
      if (this.mode === 'bundle') {
        const verschil = this.compareTotal() - this.bundleTotal();
        if (now) now.textContent = this.formatMoney(this.bundleTotal());

        /*
         * De losse waarde staat doorgestreept naast de prijs, in hetzelfde
         * element dat de gewone vergelijkprijs gebruikt. Let op: dit is de
         * optelsom van wat de onderdelen los kosten en geen eerdere vraagprijs
         * voor dit geheel; dat is een keuze van de winkel.
         */
        if (was) {
          was.textContent = this.formatMoney(this.compareTotal());
          was.hidden = verschil <= 0;
        }
        if (save) {
          save.textContent = (this.dataset.compareSave || '').replace(
            '[bedrag]',
            this.formatMoney(verschil)
          );
          save.hidden = verschil <= 0;
        }
        return;
      }

      if (now) now.textContent = variant.price_html;

      const discounted = variant.compare_at > variant.price;
      if (was) {
        was.textContent = discounted ? variant.compare_html : '';
        was.hidden = !discounted;
      }
      if (save) {
        const percent = discounted
          ? Math.round(((variant.compare_at - variant.price) / variant.compare_at) * 100)
          : 0;
        save.textContent = this.saveLabel.replace('[percentage]', percent);
        save.hidden = !discounted;
      }
    }

    paintButton(variant) {
      if (!this.button || !variant) return;

      /* Is de pouch zelf op, dan valt er ook geen bundel te kiezen. */
      this.querySelectorAll('[data-pouch-tab]').forEach((knop) => {
        knop.disabled = !variant.available;
      });

      this.button.disabled = !variant.available;
      this.button.textContent = variant.available ? this.addLabel : this.soldOutLabel;

      const sticky = document.querySelector('[data-sticky-add]');
      if (sticky) {
        sticky.disabled = !variant.available;
        sticky.textContent = this.button.textContent;
      }
    }

    /* ----------------------------------------------------------- bundel -- */

    /*
     * Bedragen opmaken volgens de notatie van de winkel. Die komt uit
     * shop.money_format en wordt niet nagebouwd: een euroteken hier hardcoderen
     * gaat stuk zodra de winkel in een tweede valuta verkoopt.
     */
    formatMoney(cents) {
      const bedrag = Math.round(cents) / 100;
      const format = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';

      const komma = bedrag.toFixed(2).replace('.', ',');
      const punt = bedrag.toFixed(2);
      const heel = String(Math.round(bedrag));

      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, komma)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, heel)
        .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, komma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, heel)
        .replace(/\{\{\s*amount\s*\}\}/g, punt);
    }

    setupBundle() {
      if (!this.bundle) return;

      this.tabs = this.querySelector('[data-pouch-tabs]');
      this.scents = this.querySelector('[data-pouch-scents]');
      if (!this.tabs || !this.scents) {
        this.bundle = false;
        return;
      }

      /* Verborgen in de HTML, hier pas zichtbaar: zonder JS valt er niets te kiezen. */
      this.tabs.hidden = false;

      /*
       * En andersom voor de losse prijsregel: die staat er voor het geval de
       * JS niet laadt, want dan blijven de kaarten verborgen en zou er geen
       * prijs op de pagina staan. Nu de kaarten er zijn draagt elke kaart zijn
       * eigen bedrag en zou deze regel hetzelfde getal herhalen.
       */
      const losseprijs = this.querySelector('[data-price]');
      if (losseprijs) losseprijs.hidden = true;

      this.bindTabs();
      this.setupScentPicker();

      /*
       * De bundel staat voorgeselecteerd. Dat is het aanbod waar de pagina naar
       * toe werkt, en wie alleen de pouch wil klikt één kaart hoger.
       *
       * Hier en niet in de HTML: setMode regelt in één keer alles wat erbij
       * hoort -- de rand op de juiste kaart, aria-checked, het aantal dat
       * verdwijnt, de geurkeuze die openklapt en de prijs die opnieuw wordt
       * gerekend. Zou de HTML de bundel al actief noemen, dan stond dat er
       * alvast half, en bij een bundel die niet compleet is zelfs onterecht.
       *
       * Na setupScentPicker, want die vult de drie sloten voor; setMode rekent
       * de bundelprijs en die telt de gekozen geuren mee.
       */
      this.setMode('bundle');
    }

    /* -------------------------------------------------------- geurkiezer -- */

    /*
     * Dezelfde dialoog als de 60 ml pagina, maar met de logica hier. De lijst
     * komt uit [data-scents], het gender uit het producttype.
     */
    setupScentPicker() {
      this.scentList = this.readScents();
      this.dialog = this.querySelector('[data-fragrance-dialog]');
      this.rowTemplate = this.querySelector('[data-fragrance-row]');
      this.searchInput = this.querySelector('[data-fragrance-search]');
      this.filter = '';
      this.query = '';
      this.activeSlot = null;

      /* De eerste drie geuren staan voorgevuld, en niet drie keer dezelfde. */
      this.querySelectorAll('[data-scent-slot]').forEach((slot, i) => {
        const keuze = this.scentList[i];
        if (keuze) this.fillSlot(slot, keuze);
      });

      this.paintFilterCounts();
      this.loadAllScents();

      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-open-scent-picker]');
        if (opener) {
          this.openScentPicker(opener.closest('[data-scent-slot]'));
          return;
        }
        if (event.target.closest('[data-close-picker]')) this.closeScentPicker();
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => {
          this.query = this.searchInput.value.trim().toLowerCase();
          this.paintRows();
        });
      }

      const tabs = this.querySelector('[data-fragrance-tabs]');
      if (tabs) {
        tabs.addEventListener('click', (event) => {
          const tab = event.target.closest('[data-filter]');
          if (!tab) return;
          this.filter = tab.getAttribute('data-filter') || '';
          tabs.querySelectorAll('[data-filter]').forEach((t) => {
            const aan = t === tab;
            t.classList.toggle('is-active', aan);
            t.setAttribute('aria-selected', aan ? 'true' : 'false');
          });
          this.paintRows();
        });
      }

      /* Buiten de kaart klikken sluit de dialoog, net als op de 60 ml pagina. */
      if (this.dialog) {
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.closeScentPicker();
        });
      }
    }

    /*
     * De rest van de geuren nahalen.
     *
     * Liquid geeft van een collectie maximaal 50 producten terug en er staan er
     * 115 in de collectie. De 50 uit de opmaak blijven de basis: die staat er
     * meteen en werkt ook als dit verzoek mislukt. Lukt het wel, dan vervangt de
     * volledige lijst hem, in de volgorde van de collectie.
     */
    loadAllScents() {
      const url = this.dataset.scentsUrl;
      if (!url || !window.fetch) return;

      fetch(url, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!data || !Array.isArray(data.products)) return;

          const volledig = [];
          data.products.forEach((product) => {
            const variants = product.variants || [];
            const variant = variants.find((v) => v.available) || null;
            /* Niet leverbaar hoort niet in de lijst, net als in de Liquid-versie. */
            if (!variant) return;

            const beeld = (product.images && product.images[0] && product.images[0].src) || '';
            volledig.push({
              id: variant.id,
              name: product.title,
              gender: product.product_type || '',
              /* products.json geeft de prijs als tekst in euro's; hier zijn het centen. */
              price: Math.round(parseFloat(variant.price) * 100),
              image: beeld ? beeld + (beeld.indexOf('?') === -1 ? '?' : '&') + 'width=112' : '',
            });
          });

          if (!volledig.length) return;

          /*
           * Het gender terugvertalen naar de taal van de filterknoppen.
           *
           * Weglot vertaalt deze feed mee: op het Nederlandse subdomein staat er
           * "Dames" en "Heren" waar de knoppen "Women" en "Men" zeggen. De
           * inline lijst uit Liquid wordt niet vertaald, dus die is wél de taal
           * van de knoppen. Dezelfde vijftig geuren zitten in allebei, en uit
           * die overlap valt per taal een kaart te bouwen: vertaald woord naar
           * het woord waar de knop op filtert.
           *
           * Zo hoeft er nergens een lijst met talen in de code te staan, en
           * blijft het kloppen als er een taal bij komt. Unisex viel niet op
           * omdat dat woord in beide talen hetzelfde is.
           */
          const genderKaart = {};
          this.scentList.forEach((canoniek) => {
            const vertaald = volledig.find((s) => s.id === canoniek.id);
            if (!vertaald || !vertaald.gender || !canoniek.gender) return;
            genderKaart[String(vertaald.gender).trim().toLowerCase()] = canoniek.gender;
          });

          volledig.forEach((scent) => {
            const sleutel = String(scent.gender || '').trim().toLowerCase();
            if (genderKaart[sleutel]) scent.gender = genderKaart[sleutel];
          });

          /*
           * Een geur die al in een slot staat maar niet in de nieuwe lijst zit,
           * gaat er alsnog bij. Anders zou selectedScents hem niet terugvinden
           * en viel een gevulde keuze stilletjes weg.
           */
          const ids = volledig.map((s) => s.id);
          this.scentList.forEach((oud) => {
            if (ids.indexOf(oud.id) === -1) volledig.push(oud);
          });

          this.scentList = volledig;
          this.paintFilterCounts();
          if (this.dialog && this.dialog.open) this.paintRows();
        })
        .catch(() => {
          /* Stil: de 50 uit de opmaak staan er nog en de kiezer werkt gewoon. */
        });
    }

    readScents() {
      const holder = this.querySelector('[data-scents]');
      if (!holder) return [];
      try {
        return JSON.parse(holder.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    openScentPicker(slot) {
      if (!slot || !this.dialog) return;
      this.activeSlot = slot;
      this.query = '';
      if (this.searchInput) this.searchInput.value = '';
      this.paintRows();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
    }

    /*
     * Sluiten, en de aandacht op het juiste slot achterlaten.
     *
     * Zonder doel is dat het slot waar de klant vandaan kwam; na een keuze is
     * het het volgende lege slot, zodat hij ziet waar hij gebleven is en meteen
     * door kan.
     *
     * focus met preventScroll en daarna zelf scrollen met block: 'nearest'.
     * Een gewone focus springt naar het element toe, en na het sluiten van een
     * modale dialoog belandde de pagina daardoor bovenaan. Met 'nearest' blijft
     * de pagina staan als het slot al in beeld is, en schuift hij alleen als
     * dat nodig is.
     */
    closeScentPicker(doel) {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');

      const slot = doel || this.activeSlot;
      const opener = slot && slot.querySelector('[data-open-scent-picker]');
      if (!opener) return;

      try {
        opener.focus({ preventScroll: true });
      } catch (error) {
        opener.focus();
      }

      if (opener.scrollIntoView) {
        opener.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    /*
     * Het eerstvolgende slot dat nog leeg is, gerekend vanaf het slot dat net is
     * ingevuld. Zijn ze allemaal vol, dan blijft de klant staan waar hij staat;
     * dan is de volgende stap de koopknop en niet nog een geur.
     */
    nextEmptySlot() {
      const slots = Array.prototype.slice.call(this.querySelectorAll('[data-scent-slot]'));
      if (!slots.length) return null;

      const vanaf = Math.max(0, slots.indexOf(this.activeSlot));
      for (let i = 1; i <= slots.length; i += 1) {
        const kandidaat = slots[(vanaf + i) % slots.length];
        const invoer = kandidaat.querySelector('[data-scent-variant]');
        if (invoer && !invoer.value) return kandidaat;
      }
      return null;
    }

    matchingScents() {
      return this.scentList.filter((scent) => {
        /*
         * Hoofdletterongevoelig. Het producttype komt uit de admin en staat
         * daar niet overal gelijk -- Ceylon Couture heeft "men" met een kleine
         * letter en viel daardoor uit de herenlijst.
         */
        if (this.filter && !sameGender(scent.gender, this.filter)) return false;
        if (this.query && String(scent.name).toLowerCase().indexOf(this.query) === -1) return false;
        return true;
      });
    }

    paintFilterCounts() {
      this.querySelectorAll('[data-count-for]').forEach((teller) => {
        const gender = teller.getAttribute('data-count-for');
        const aantal = gender
          ? this.scentList.filter((s) => sameGender(s.gender, gender)).length
          : this.scentList.length;
        teller.textContent = aantal ? ' ' + aantal : '';
      });
    }

    paintRows() {
      const lijst = this.querySelector('[data-fragrance-list]');
      const leeg = this.querySelector('[data-fragrance-empty]');
      if (!lijst || !this.rowTemplate) return;

      const treffers = this.matchingScents();
      lijst.textContent = '';

      treffers.forEach((scent) => {
        const rij = this.rowTemplate.content.cloneNode(true);
        const img = rij.querySelector('.fragrance-row__thumb img');
        if (img) {
          img.src = scent.image || '';
          img.alt = '';
        }
        const naam = rij.querySelector('.fragrance-row__name');
        if (naam) naam.textContent = scent.name;
        const pil = rij.querySelector('.fragrance-row__pill');
        if (pil) pil.textContent = scent.gender || '';
        const knop = rij.querySelector('.fragrance-row__choose');
        if (knop) {
          knop.addEventListener('click', () => {
            this.fillSlot(this.activeSlot, scent);
            /* Eerst vullen, dan pas het volgende lege slot zoeken. */
            this.closeScentPicker(this.nextEmptySlot());
            this.paintTabs();
            this.paintPrice(this.current);
            this.hideError();
          });
        }
        lijst.appendChild(rij);
      });

      if (leeg) leeg.hidden = treffers.length > 0;
    }

    fillSlot(slot, scent) {
      if (!slot || !scent) return;
      const invoer = slot.querySelector('[data-scent-variant]');
      const label = slot.querySelector('[data-scent-label]');
      const thumb = slot.querySelector('[data-scent-thumb] img');
      if (invoer) invoer.value = scent.id;
      if (label) label.textContent = scent.name;
      if (thumb && scent.image) thumb.src = scent.image;

      /*
       * Prijs en korting van deze geur. Zelfde percentage en dezelfde afronding
       * als bundleTotal, want anders telt de klant drie regels op en komt hij
       * niet uit op het bedrag dat er boven de kaart staat.
       */
      const pricing = slot.querySelector('[data-scent-pricing]');
      if (pricing) {
        const percent = Number(this.dataset.bundleScentPercent) || 0;
        const nu = this.discounted(scent.price, percent);

        const was = slot.querySelector('[data-scent-was]');
        if (was) {
          was.textContent = this.formatMoney(scent.price);
          was.hidden = nu >= scent.price;
        }

        const off = slot.querySelector('[data-scent-off]');
        if (off) {
          off.textContent = '\u2212' + percent + '%';
          off.hidden = percent <= 0 || nu >= scent.price;
        }

        const nuEl = slot.querySelector('[data-scent-now]');
        if (nuEl) nuEl.textContent = this.formatMoney(nu);

        pricing.hidden = false;
      }

      slot.classList.add('is-filled');
    }

    /*
     * Toetsenbediening volgens de gangbare praktijk voor een radiogroep: de
     * pijltjes wisselen, en de kaart die aan staat is de enige met tabindex 0,
     * zodat je met Tab de lijst in en uit stapt in plaats van er doorheen.
     *
     * Omhoog en omlaag én links en rechts: de kaarten staan onder elkaar, maar
     * wie van de oude tabrij komt drukt links en rechts, en dat mag blijven
     * werken.
     */
    bindTabs() {
      const knoppen = Array.prototype.slice.call(this.querySelectorAll('[data-pouch-tab]'));

      knoppen.forEach((knop, i) => {
        knop.tabIndex = i === 0 ? 0 : -1;

        knop.addEventListener('click', () => this.setMode(knop.dataset.pouchTab));

        knop.addEventListener('keydown', (event) => {
          let stap = 0;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') stap = 1;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') stap = -1;
          if (!stap) return;
          event.preventDefault();
          const doel = knoppen[(i + stap + knoppen.length) % knoppen.length];
          doel.focus();
          this.setMode(doel.dataset.pouchTab);
        });
      });
    }

    setMode(mode) {
      if (!this.bundle || (mode !== 'single' && mode !== 'bundle')) return;
      this.mode = mode;

      this.querySelectorAll('[data-pouch-tab]').forEach((knop) => {
        const aan = knop.dataset.pouchTab === mode;

        /*
         * De rand hoort om de hele kaart en niet alleen om de kop, want de
         * geurkeuze staat er in dezelfde kaart onder. Vandaar een niveau
         * omhoog; zonder kaart valt het terug op de knop zelf.
         */
        const kaart = knop.closest('[data-pouch-plan]') || knop;
        kaart.classList.toggle('is-active', aan);

        knop.setAttribute('aria-checked', aan ? 'true' : 'false');
        knop.tabIndex = aan ? 0 : -1;
      });

      if (this.scents) this.scents.hidden = mode !== 'bundle';
      this.querySelectorAll('[data-single-only]').forEach((el) => {
        el.hidden = mode === 'bundle';
      });

      this.paintPrice(this.current);
      this.paintButton(this.current);
      this.hideError();
    }

    /* De gekozen geur per slot, in slotvolgorde. */
    selectedScents() {
      return Array.prototype.map
        .call(this.querySelectorAll('[data-scent-slot]'), (slot) => {
          const invoer = slot.querySelector('[data-scent-variant]');
          if (!invoer || !invoer.value) return null;
          const id = Number(invoer.value);
          return this.scentList.find((scent) => scent.id === id) || null;
        })
        .filter(Boolean);
    }

    giftVariantIds() {
      return (this.dataset.bundleGiftVariants || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
        .map(Number);
    }

    /*
     * Wat de bundel kost: de pouch met korting, de drie geuren met korting, en
     * de capsules gratis.
     *
     * Deze percentages zijn een kopie. Wat er werkelijk wordt afgerekend komt
     * uit de discount function in het app sentir-discounts, in
     * extensions/volume-discount. Wijzig je daar iets, wijzig het hier dan ook
     * -- en andersom. Staat het uit elkaar, dan belooft deze pagina een bedrag
     * dat de kassa niet rekent.
     */
    discounted(price, percent) {
      /*
       * Eerst het kortingsbedrag, dan aftrekken, en naar beneden afronden.
       * Shopify kapt de korting af op hele centen; andersom gerekend staat de
       * pagina een cent onder de kassa. Zelfde volgorde als in
       * snippets/bundle-picker-form.liquid en snippets/cart-drawer-item.liquid.
       */
      const amount = Math.floor((price * percent) / 100);
      return price - amount;
    }

    bundleTotal() {
      const pouchPercent = Number(this.dataset.bundlePouchPercent) || 0;
      const scentPercent = Number(this.dataset.bundleScentPercent) || 0;

      const basis = this.current ? this.discounted(this.current.price, pouchPercent) : 0;
      return this.selectedScents().reduce(
        (op, scent) => op + this.discounted(scent.price, scentPercent),
        basis
      );
    }

    /*
     * Wat dezelfde spullen los zouden kosten: de volle pouch, de volle geuren
     * en drie twistcapsules. Nadrukkelijk de volle prijzen en niet bundleTotal
     * plus capsules -- dat laatste zou de korting uit de vergelijking laten
     * vallen en de besparing veel te laag laten lijken.
     */
    compareTotal() {
      const per = Number(this.dataset.bundleGiftValue) || 0;
      const basis = this.current ? this.current.price : 0;
      const scents = this.selectedScents().reduce((op, scent) => op + scent.price, 0);
      return basis + scents + per * 3;
    }

    paintTabs() {
      if (!this.bundle) return;

      const enkel = this.querySelector('[data-tab-price-single]');
      const bundel = this.querySelector('[data-tab-price-bundle]');
      if (enkel && this.current) enkel.textContent = this.formatMoney(this.current.price);
      if (bundel) bundel.textContent = this.formatMoney(this.bundleTotal());

      /*
       * De doorgestreepte bedragen staan op de kaarten zelf. Met een bundel
       * toont het koopblok geen losse prijsregel meer -- die zou dezelfde prijs
       * een tweede keer laten zien -- dus is dit de enige plek waar ze nog
       * kunnen staan.
       *
       * Bij de pouch is dat de vergelijkprijs uit Shopify. Bij de bundel is het
       * de optelsom van wat de onderdelen los kosten en dus geen eerdere
       * vraagprijs voor dit geheel; dat is een keuze van de winkel.
       */
      const enkelWas = this.querySelector('[data-plan-was-single]');
      if (enkelWas) {
        const korting = this.current && this.current.compare_at > this.current.price;
        enkelWas.textContent = korting ? this.current.compare_html : '';
        enkelWas.hidden = !korting;
      }

      const bundelWas = this.querySelector('[data-plan-was]');
      if (bundelWas) {
        const voordeel = this.compareTotal() - this.bundleTotal();
        bundelWas.textContent = this.formatMoney(this.compareTotal());
        bundelWas.hidden = voordeel <= 0;
      }

      /*
       * Het percentage achter de naam. Zelfde afronding als de bundelkaart in
       * het mandje en als de rest van het thema: naar beneden, behalve als het
       * er net onder zit. Anders zegt de pagina min 52 en het mandje min 53
       * voor precies hetzelfde mandje.
       */
      const off = this.querySelector('[data-plan-off]');
      if (off) {
        const vergelijk = this.compareTotal();
        const winst = vergelijk - this.bundleTotal();
        let procent = 0;
        if (vergelijk > 0 && winst > 0) {
          procent = Math.floor((winst * 100) / vergelijk + 0.25);
        }
        off.textContent = '\u2212' + procent + '%';
        off.hidden = procent <= 0;
      }

      this.paintPouchLine();

      const badge = this.querySelector('[data-tab-badge]');
      if (badge) {
        const voordeel = this.compareTotal() - this.bundleTotal();
        const sjabloon = this.dataset.bundleBadge || '';
        badge.textContent = sjabloon.replace('[bedrag]', this.formatMoney(voordeel));
        badge.hidden = voordeel <= 0 || sjabloon === '';
      }
    }

    /*
     * De pouchregel bovenaan de bundelkaart. Volgt de kleurkeuze hierboven:
     * foto, kleurnaam en prijs veranderen mee, want dit is dezelfde pouch en
     * niet een tweede keuze.
     */
    paintPouchLine() {
      const rij = this.querySelector('[data-pouch-line-pricing]');
      if (!rij || !this.current) return;

      const percent = Number(this.dataset.bundlePouchPercent) || 0;
      const nu = this.discounted(this.current.price, percent);

      const was = this.querySelector('[data-pouch-line-was]');
      if (was) {
        was.textContent = this.formatMoney(this.current.price);
        was.hidden = nu >= this.current.price;
      }

      const off = this.querySelector('[data-pouch-line-off]');
      if (off) {
        off.textContent = '\u2212' + percent + '%';
        off.hidden = percent <= 0 || nu >= this.current.price;
      }

      const nuEl = this.querySelector('[data-pouch-line-now]');
      if (nuEl) nuEl.textContent = this.formatMoney(nu);

      /*
       * De kleurnaam komt uit de variant en niet uit de aangeklikte knop: bij
       * meer dan één optie staat in variant.title de hele combinatie, en dat is
       * precies wat de klant koopt.
       */
      const naam = this.querySelector('[data-pouch-line-variant]');
      if (naam && this.current.title) naam.textContent = this.current.title;

      const foto = this.querySelector('[data-pouch-line-image]');
      if (foto && this.current.image) foto.src = this.current.image;
    }

    /*
     * De regels voor het mandje. Zeven stuks: de pouch, drie geuren en drie
     * cadeaucapsules. Ze dragen alle drie hetzelfde stempel mee, zodat in de
     * order te zien is dat ze bij elkaar horen en twee bundels in één mandje
     * uit elkaar te houden zijn. De underscore houdt ze uit het zicht van de
     * klant.
     */
    bundleItems() {
      const variant = this.current;
      const scents = this.selectedScents();
      const gifts = this.giftVariantIds();
      const stamp = 'pouch-' + Date.now().toString(36);
      const merk = { _bundle: 'pouch3', _bundle_id: stamp };

      const items = [{ id: variant.id, quantity: 1, properties: Object.assign({}, merk) }];

      scents.forEach((scent) => {
        items.push({ id: scent.id, quantity: 1, properties: Object.assign({}, merk) });
      });

      /*
       * Eén capsule per geur, verdeeld over de ingestelde varianten. Staat er
       * één kleur ingesteld, dan is dat één regel van drie -- in het mandje een
       * regel met "x3" in plaats van drie regels die hetzelfde zeggen. Staan er
       * drie kleuren, dan is het weer één stuk per kleur.
       *
       * De rest gaat naar de eerste varianten, zodat het totaal altijd op het
       * aantal geuren uitkomt en niet op een afronding.
       */
      const slots = Number(this.dataset.bundleSlots) || 3;
      gifts.forEach((giftId, i) => {
        const basis = Math.floor(slots / gifts.length);
        const extra = i < slots % gifts.length ? 1 : 0;
        const aantal = basis + extra;
        if (aantal <= 0) return;
        items.push({
          id: giftId,
          quantity: aantal,
          properties: Object.assign({ _bundle_gift: '1' }, merk),
        });
      });

      return items;
    }

    /* ---------------------------------------------------------- galerij -- */

    /*
     * De galerij staat buiten dit element -- hij hoort in de linkerkolom -- dus
     * hij wordt hier opgezocht en niet via this.
     */
    gallery() {
      const root = document.querySelector('[data-pouch-gallery]');
      if (!root) return null;
      return {
        root: root,
        stage: root.querySelector('.bp-gallery__stage'),
        thumbs: root.querySelector('[data-thumbs]'),
        images: Array.prototype.slice.call(root.querySelectorAll('.bp-gallery__image')),
        cards: Array.prototype.slice.call(root.querySelectorAll('.bp-gallery__thumb')),
        radios: Array.prototype.slice.call(root.querySelectorAll('.bp-gallery__radio')),
      };
    }

    /*
     * De pijlen staan verborgen in de HTML en komen hier tevoorschijn: zonder
     * JavaScript schuift de strook met vinger of trackpad, en dan hoort er
     * geen knop te staan die niets doet.
     */
    setupGallery() {
      const g = this.gallery();
      if (!g) return;

      g.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-thumbs-prev], [data-photo-prev]')) this.stepPhoto(-1);
        if (event.target.closest('[data-thumbs-next], [data-photo-next]')) this.stepPhoto(1);
      });

      /*
       * Een miniatuur is een <label> voor een radio die buiten beeld staat,
       * bovenaan de galerij. Laat je de browser zijn gang gaan, dan krijgt die
       * radio focus en scrollt hij in beeld -- en dus springt de pagina naar de
       * bovenkant van de galerij. Daarom vangen we de klik af en zetten we de
       * radio zelf om; zonder JavaScript doet het label gewoon zijn werk.
       */
      if (g.thumbs) {
        g.thumbs.addEventListener('click', (event) => {
          const kaart = event.target.closest('.bp-gallery__thumb');
          if (!kaart) return;
          event.preventDefault();
          const plek = g.cards.indexOf(kaart);
          if (plek !== -1) this.openPhoto(plek);
        });
      }

      /* Met de pijltjestoetsen door de radio's: dan moet de strook mee. */
      g.root.addEventListener('change', (event) => {
        if (!event.target.matches('.bp-gallery__radio')) return;
        this.syncNav();
        this.scrollToOpen();
      });

      this.setupSwipe(g.stage);
      this.syncNav();
    }

    /*
     * Vegen over de hoofdfoto. De luisteraars zijn passief en er wordt niets
     * geblokkeerd: pas bij het loslaten wordt er gekeken, en alleen als de
     * beweging duidelijk horizontaal was. Verticaal scrollen blijft dus gewoon
     * scrollen, ook als je de foto raakt.
     */
    setupSwipe(stage) {
      if (!stage) return;
      let startX = null;
      let startY = null;

      stage.addEventListener(
        'touchstart',
        (event) => {
          const aanraking = event.changedTouches[0];
          startX = aanraking.clientX;
          startY = aanraking.clientY;
        },
        { passive: true }
      );

      stage.addEventListener(
        'touchend',
        (event) => {
          if (startX === null) return;
          const aanraking = event.changedTouches[0];
          const dx = aanraking.clientX - startX;
          const dy = aanraking.clientY - startY;
          startX = null;
          if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
          this.stepPhoto(dx < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }

    /* De plekken die op dit moment te zien zijn, op volgorde. */
    visibleIndexes() {
      const g = this.gallery();
      if (!g) return [];
      return g.images.reduce((op, img, i) => {
        if (!img.hidden) op.push(i);
        return op;
      }, []);
    }

    openIndex() {
      const g = this.gallery();
      if (!g) return -1;
      let open = -1;
      g.radios.forEach((radio, i) => {
        if (radio.checked) open = i;
      });
      return open;
    }

    openPhoto(index) {
      const g = this.gallery();
      if (!g || !g.radios[index]) return;
      g.radios[index].checked = true;
      this.syncNav();
      this.scrollToOpen();
    }

    /*
     * De reeks is rond: voorbij de laatste foto begint hij weer vooraan, en
     * andersom. Doorklikken loopt dus nooit dood.
     */
    stepPhoto(richting) {
      const zichtbaar = this.visibleIndexes();
      if (zichtbaar.length < 2) return;
      const plek = zichtbaar.indexOf(this.openIndex());
      if (plek === -1) return;
      const doel = (plek + richting + zichtbaar.length) % zichtbaar.length;
      this.openPhoto(zichtbaar[doel]);
    }

    /*
     * De open miniatuur in beeld schuiven. Met getBoundingClientRect en niet
     * met scrollIntoView: die laatste kan ook de pagina zelf verticaal
     * verschuiven, en dan springt de hele productpagina bij het doorklikken.
     */
    scrollToOpen() {
      const g = this.gallery();
      if (!g || !g.thumbs) return;
      const kaart = g.cards[this.openIndex()];
      if (!kaart || kaart.hidden) return;

      const strook = g.thumbs.getBoundingClientRect();
      const vak = kaart.getBoundingClientRect();
      if (vak.left < strook.left) {
        g.thumbs.scrollLeft -= strook.left - vak.left;
      } else if (vak.right > strook.right) {
        g.thumbs.scrollLeft += vak.right - strook.right;
      }
    }

    /*
     * De pijlen doen het altijd -- de reeks is rond -- dus er valt niets uit te
     * schakelen. Ze verschijnen hier wel pas als er iets te doorlopen valt: bij
     * één zichtbare foto zouden het knoppen zijn die naar zichzelf wijzen.
     *
     * Dit is meteen de plek waar ze uit hun verborgen begintoestand komen, dus
     * zonder JavaScript blijven ze weg.
     */
    syncNav() {
      const g = this.gallery();
      if (!g) return;
      const meerdere = this.visibleIndexes().length > 1;

      g.root
        .querySelectorAll('[data-thumbs-prev], [data-thumbs-next], [data-photo-prev], [data-photo-next]')
        .forEach((knop) => {
          knop.hidden = !meerdere;
        });
    }

    /*
     * De galerij toont alleen de foto's van de gekozen kleur.
     *
     * Welke foto bij welke kleur hoort heeft de sectie al bepaald en in
     * data-colour-group gezet; hier gaat alleen aan en uit. De radio's blijven
     * staan: de zichtbaarheid loopt via het hidden-attribuut, en de
     * nth-child-regels in de sectie tellen gewoon door over verborgen foto's
     * heen, dus de koppeling radio-foto blijft kloppen.
     *
     * Een kleur zonder eigen foto's krijgt ze allemaal te zien. Zolang er nog
     * maar één kleur is gefotografeerd is een volle galerij eerlijker dan een
     * lege.
     */
    paintGallery(variant) {
      const g = this.gallery();
      if (!g || !g.images.length) return;

      const groep = String(variant.id);
      const heeftEigen = g.images.some(function (img) {
        return img.getAttribute('data-colour-group') === groep;
      });

      const zichtbaar = g.images.map(function (img) {
        if (!heeftEigen) return true;
        const eigenaar = img.getAttribute('data-colour-group') || '';
        return eigenaar === '' || eigenaar === groep;
      });

      g.images.forEach(function (img, i) {
        img.hidden = !zichtbaar[i];
      });
      g.cards.forEach(function (kaart, i) {
        kaart.hidden = !zichtbaar[i];
      });

      /*
       * Openen op de eerste foto van de kleur zelf, niet op de eerste
       * zichtbare. Een gedeelde openingsfoto blijft bij elke kleur staan, en
       * daarop blijven hangen zou het wisselen van kleur laten lijken alsof er
       * niets gebeurt.
       */
      let doel = -1;
      g.images.forEach(function (img, i) {
        if (doel === -1 && zichtbaar[i] && img.getAttribute('data-colour-group') === groep) doel = i;
      });
      if (doel === -1) doel = zichtbaar.indexOf(true);
      if (doel !== -1) this.openPhoto(doel);
    }

    /* Het adres meeschrijven, zodat delen en terugkomen op dezelfde kleur uitkomt. */
    paintUrl(variant) {
      if (!this.dataset.url || !window.history || !window.history.replaceState) return;
      try {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.pathname + url.search);
      } catch (error) {
        /* Een adresbalk die niet meeloopt mag de pagina niet breken. */
      }
    }

    /* ------------------------------------------------------------ aantal -- */

    quantity() {
      /* Een bundel is er één. De teller staat op tab 2 niet eens in beeld. */
      if (this.mode === 'bundle') return 1;
      const value = parseInt(this.quantityInput ? this.quantityInput.value : '1', 10);
      if (!value || value < 1) return 1;
      return value;
    }

    stepQuantity(step) {
      if (!this.quantityInput) return;
      this.quantityInput.value = Math.max(1, this.quantity() + step);
      this.syncQuantityButtons();
    }

    /* Leeggemaakt of "abc" ingetypt: terug naar één in plaats van naar niets. */
    normaliseQuantity() {
      if (!this.quantityInput) return;
      this.quantityInput.value = this.quantity();
      this.syncQuantityButtons();
    }

    syncQuantityButtons() {
      const down = this.querySelector('[data-quantity-down]');
      if (down) down.disabled = this.quantity() <= 1;
    }

    /* --------------------------------------------------------- meldingen -- */

    showError(message) {
      if (!this.errorNote) return;
      this.errorNote.textContent = message;
      this.errorNote.hidden = false;
    }

    hideError() {
      if (!this.errorNote) return;
      this.errorNote.hidden = true;
    }

    /*
     * De vaste balk op mobiel verschijnt zodra de echte knop uit beeld is. Met
     * een observer en niet op scrollpositie, net als op de 60 ml pagina.
     */
    watchStickyBar() {
      const bar = document.querySelector('[data-sticky-bar]');
      if (!bar || !this.button || typeof IntersectionObserver !== 'function') return;

      bar.hidden = false;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            bar.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { rootMargin: '0px 0px -40px 0px' }
      );

      observer.observe(this.button);

      bar.addEventListener('click', (event) => {
        /*
         * De kleurknoppen zetten niets zelf: ze zoeken de radio in het koopblok
         * en klikken die aan. Dan loopt de wissel langs precies dezelfde weg als
         * een klik op de staal zelf -- galerij, prijs, knop, URL -- en is er geen
         * tweede plek die de gekozen kleur bijhoudt.
         */
        const kleur = event.target.closest('[data-sticky-colour]');
        if (kleur) {
          event.preventDefault();
          const waarde = kleur.dataset.stickyColour;
          const radio = Array.prototype.find.call(
            this.querySelectorAll('.pouch-swatch__radio'),
            (invoer) => invoer.value === waarde
          );
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        if (!event.target.closest('[data-sticky-add]')) return;
        event.preventDefault();
        this.addToCart();
      });
    }

    /* ------------------------------------------------------------ mandje -- */

    addToCart() {
      const variant = this.current;
      if (!variant || !variant.available) return;

      /*
       * Eén weg naar het mandje voor beide modi. De envelop was al
       * { items: [...] }, dus een bundel is niets anders dan een langere lijst;
       * aan het contract met SentirCart verandert niets.
       */
      let items;
      if (this.mode === 'bundle') {
        const scents = this.selectedScents();
        const slots = Number(this.dataset.bundleSlots) || 3;
        if (scents.length < slots) {
          this.showError(this.dataset.errorScents || '');
          return;
        }
        items = this.bundleItems();
      } else {
        items = [{ id: variant.id, quantity: this.quantity() }];
      }

      if (this.button) this.button.disabled = true;
      const done = () => {
        if (this.button) this.button.disabled = false;
      };

      /*
       * Mislukt het, dan komt er niets in het mandje: /cart/add.js voegt een
       * lijst in zijn geheel toe of helemaal niet. Een halve bundel kan dus niet
       * ontstaan. De melding is wel een andere, want in bundelmodus is de
       * waarschijnlijke oorzaak dat een geur of een cadeaucapsule net op is.
       */
      const misgegaan = (error) => {
        done();
        const eigen = error && error.message;
        if (this.mode === 'bundle') {
          this.showError(this.dataset.errorGift || eigen || '');
          return;
        }
        this.showError(eigen || '');
      };

      if (window.SentirCart && typeof window.SentirCart.add === 'function') {
        window.SentirCart.add({ items: items }).then(done, misgegaan);
        return;
      }

      // Zonder de drawer alsnog toevoegen en doorsturen naar het mandje.
      fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('status ' + response.status);
          window.location.href = ROOT + 'cart';
        })
        .catch(misgegaan);
    }
  }

  if (!customElements.get('pouch-picker')) {
    customElements.define('pouch-picker', PouchPicker);
  }

  /*
   * Trustpilot in een dichtgeklapt paneel.
   *
   * Zelfde vangnet als in bundle-picker.js: de widget meet zichzelf bij het
   * laden op, en in een gesloten <details> is dat nul bij nul -- het vak blijft
   * daarna leeg, ook als je het paneel opent. Daarom pas mounten bij het
   * openklappen.
   *
   * Bewust hier herhaald en niet gedeeld: de 60 ml pagina laadt bundle-picker.js
   * en deze pagina niet, en dat bestand aanpassen zou een levende pagina raken
   * voor een blok dat hier op zichzelf staat.
   */
  function mountDeferredTrustpilot(scope) {
    const widget = scope.querySelector('[data-tp-defer]:not([data-tp-mounted])');
    if (!widget) return;
    if (!window.Trustpilot || typeof window.Trustpilot.loadFromElement !== 'function') return;

    window.Trustpilot.loadFromElement(widget, true);
    widget.setAttribute('data-tp-mounted', '');
  }

  function watchDeferredWidgets() {
    document.querySelectorAll('details').forEach((panel) => {
      if (!panel.querySelector('[data-tp-defer]')) return;

      panel.addEventListener('toggle', () => {
        if (!panel.open) return;
        mountDeferredTrustpilot(panel);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDeferredWidgets);
  } else {
    watchDeferredWidgets();
  }
})();
